/**
 * ポイント購入直後マーカー (sessionStorage)
 *
 * 残高補充後のライブ反映 (フルリロード不要化) のための受け渡し手段。
 * docs/requirements/credit-balance-live-refresh-plan.md §4 参照。
 *
 * - `expectedPoints` / `baselineBalance` は Stripe の戻りクエリでは復元できないため、
 *   `stripe.confirmPayment` の直前にこのマーカーへ書く。
 * - 3DS リダイレクト等でページ遷移をまたぐ経路 (経路 B) では、復帰先がこれを拾って
 *   ポーリングを起動する。読んだら即削除し、TTL 超過の古いマーカーは破棄する。
 */

const MARKER_KEY = 'creditPurchasePending';

/** マーカーの有効期限。これを過ぎたら誤起動を避けるため破棄する。 */
const MARKER_TTL_MS = 5 * 60 * 1000;

/** 購入直後のライブ反映ポーリングに必要な情報。 */
export interface CreditPurchaseInfo {
  /** 購入ポイント数 */
  expectedPoints: number;
  /**
   * 購入開始時点の合計利用可能ポイント (total_available)。
   * baseline を能動 fetch できなかった場合は null (= 不明 → 閾値判定をしない固定ポーリング)。
   */
  baselineBalance: number | null;
  /** Stripe PaymentIntent ID (将来の intent ベース判定の布石。任意) */
  paymentIntentId?: string;
}

export interface CreditPurchaseMarker extends CreditPurchaseInfo {
  /** 書き込み時刻 (epoch ms)。TTL 判定に使う。 */
  ts: number;
}

/**
 * マーカーを書き込む。`confirmPayment` の直前に呼ぶこと。
 * (PaymentIntent 作成直後に書くと、支払いフォームを開いただけでキャンセルした
 *  ケースでも残ってしまうため避ける)
 */
export function writePurchaseMarker(marker: CreditPurchaseMarker): void {
  try {
    sessionStorage.setItem(MARKER_KEY, JSON.stringify(marker));
  } catch {
    // sessionStorage が使えない環境 (プライベートブラウズ等) では黙ってスキップ。
    // マーカーが無くても既存の定期 refetch / フォーカス再取得でいずれ反映される。
  }
}

/**
 * マーカーを読み取る (削除はしない)。TTL 超過・破損時は破棄して null を返す。
 */
export function peekPurchaseMarker(): CreditPurchaseMarker | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(MARKER_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: CreditPurchaseMarker | null = null;
  try {
    parsed = JSON.parse(raw) as CreditPurchaseMarker;
  } catch {
    clearPurchaseMarker();
    return null;
  }

  if (
    !parsed ||
    typeof parsed.expectedPoints !== 'number' ||
    (typeof parsed.baselineBalance !== 'number' && parsed.baselineBalance !== null) ||
    typeof parsed.ts !== 'number' ||
    Date.now() - parsed.ts > MARKER_TTL_MS
  ) {
    clearPurchaseMarker();
    return null;
  }

  return parsed;
}

/**
 * マーカーを読み取って即削除 (消費) する。起動経路で使う。
 */
export function consumePurchaseMarker(): CreditPurchaseMarker | null {
  const marker = peekPurchaseMarker();
  clearPurchaseMarker();
  return marker;
}

/** マーカーを削除する。失敗・中断系では必ず呼ぶ。 */
export function clearPurchaseMarker(): void {
  try {
    sessionStorage.removeItem(MARKER_KEY);
  } catch {
    // no-op
  }
}
